import React, { useEffect, useMemo, useState } from "react";
import { FaStar } from "react-icons/fa";
import { getEvent } from "../../api/event";
import bookingApi from "../../api/booking";
import reviewApi from "../../api/review";

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const buildSubmittedKey = ({ eventId, reviewerId, revieweeId }) =>
  `post-event-review:${eventId || "na"}:${reviewerId || "na"}:${revieweeId || "na"}`;

const PostEventReview = ({ eventId, currentUser, otherUser }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [revieweeId, setRevieweeId] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const reviewerId = currentUser?._id;
  const otherUserId = otherUser?._id;

  const revieweeName = useMemo(
    () => otherUser?.name || "this professional",
    [otherUser?.name],
  );

  const revieweeAvatar = useMemo(
    () =>
      otherUser?.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(revieweeName || "User")}`,
    [otherUser?.avatar, revieweeName],
  );

  useEffect(() => {
    let isMounted = true;

    const hydrateEligibility = async () => {
      if (!eventId || !reviewerId || !otherUserId) {
        if (isMounted) {
          setIsEligible(false);
        }
        return;
      }

      setIsLoading(true);

      try {
        const [eventRes, userBookingsRes, ownerBookingsRes] =
          await Promise.allSettled([
            getEvent(eventId),
            bookingApi.getUserBookings(reviewerId, {
              status: "confirmed",
              limit: 100,
            }),
            bookingApi.getOwnerBookings(reviewerId, {
              status: "confirmed",
              limit: 100,
            }),
          ]);

        const eventPayload =
          eventRes?.status === "fulfilled" ? eventRes.value : null;
        const eventData = eventPayload?.data || eventPayload || {};

        const userBookings =
          userBookingsRes?.status === "fulfilled"
            ? userBookingsRes.value?.data?.bookings || []
            : [];
        const ownerBookings =
          ownerBookingsRes?.status === "fulfilled"
            ? ownerBookingsRes.value?.data?.bookings || []
            : [];

        const allBookings = [...userBookings, ...ownerBookings];
        const matchedBooking = allBookings.find((booking) => {
          const bookingEventId = getId(booking?.event);
          return bookingEventId === String(eventId);
        });

        const bookingStatus = (
          matchedBooking?.bookingStatus ||
          matchedBooking?.status ||
          ""
        ).toLowerCase();
        const eventStatus = String(eventData?.status || "").toLowerCase();
        const eventDateValue =
          eventData?.eventDate ||
          eventData?.startDate ||
          matchedBooking?.event?.eventDate ||
          matchedBooking?.event?.startDate;

        const eventDate = eventDateValue ? new Date(eventDateValue) : null;
        const isDateValid = !!eventDate && !Number.isNaN(eventDate.getTime());
        const isCompleted = isDateValid && new Date() > eventDate;

        const hostId = getId(matchedBooking?.host || eventData?.customer);
        const providerId = getId(
          matchedBooking?.serviceProvider || eventData?.assignedTo,
        );

        const isParticipant =
          reviewerId === hostId || reviewerId === providerId;

        const isChatParticipantMatch =
          otherUserId === hostId || otherUserId === providerId;

        const shouldShow =
          bookingStatus === "confirmed" &&
          eventStatus !== "cancelled" &&
          isCompleted &&
          isParticipant &&
          isChatParticipantMatch;

        let resolvedRevieweeId = "";
        if (reviewerId === hostId) {
          resolvedRevieweeId = providerId;
        } else if (reviewerId === providerId) {
          resolvedRevieweeId = hostId;
        }

        if (!resolvedRevieweeId) {
          resolvedRevieweeId = otherUserId;
        }

        if (!isMounted) return;

        setRevieweeId(resolvedRevieweeId);
        setIsEligible(shouldShow);

        if (shouldShow && resolvedRevieweeId) {
          const localSubmitted =
            localStorage.getItem(
              buildSubmittedKey({
                eventId,
                reviewerId,
                revieweeId: resolvedRevieweeId,
              }),
            ) === "1";

          if (localSubmitted) {
            setIsSubmitted(true);
            return;
          }

          try {
            const existingReview = await reviewApi.getMyReviewForEvent({
              eventId,
              reviewerId,
              revieweeId: resolvedRevieweeId,
            });

            if (existingReview) {
              setIsSubmitted(true);
              localStorage.setItem(
                buildSubmittedKey({
                  eventId,
                  reviewerId,
                  revieweeId: resolvedRevieweeId,
                }),
                "1",
              );
            }
          } catch (error) {
            // Keep UI functional even when review listing endpoint is unavailable.
          }
        }
      } catch (error) {
        if (isMounted) {
          setIsEligible(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    hydrateEligibility();

    return () => {
      isMounted = false;
    };
  }, [eventId, reviewerId, otherUserId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !eventId ||
      !reviewerId ||
      !revieweeId ||
      rating < 1 ||
      isSubmitting ||
      isSubmitted
    ) {
      return;
    }

    try {
      setIsSubmitting(true);
      await reviewApi.createReview({
        eventId,
        reviewerId,
        revieweeId,
        rating,
        comment: comment?.trim() || "",
      });

      setIsSubmitted(true);
      localStorage.setItem(
        buildSubmittedKey({ eventId, reviewerId, revieweeId }),
        "1",
      );
    } catch (error) {
      // Keep failure non-blocking for chat experience.
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!eventId || isLoading || !isEligible) {
    return null;
  }

  return (
    <div className="px-3 sm:px-5 pb-4">
      <div className="max-w-xl mx-auto bg-white border border-orange-100 rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-3 mb-4">
          <img
            src={revieweeAvatar}
            alt={revieweeName}
            className="w-11 h-11 rounded-full object-cover border border-orange-100"
          />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              How was your experience with {revieweeName}?
            </p>
            <p className="text-xs text-gray-500">
              Share your post-event feedback
            </p>
          </div>
        </div>

        {isSubmitted ? (
          <div className="rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium px-3 py-2">
            Review Submitted ✅
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="text-xl"
                    aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                    disabled={isSubmitting}
                  >
                    <FaStar
                      className={
                        star <= rating ? "text-[#FF4D12]" : "text-gray-300"
                      }
                    />
                  </button>
                ))}
                <span className="ml-2 text-xs text-gray-500">
                  {rating || 0}/5
                </span>
              </div>
            </div>

            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your feedback..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#FF4D12]/40 focus:bg-white"
              disabled={isSubmitting}
            />

            <button
              type="submit"
              disabled={isSubmitting || rating < 1}
              className="h-10 px-4 rounded-xl bg-[#FF4D12] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#e74510] transition"
            >
              {isSubmitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PostEventReview;
